import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Keyboard } from 'lucide-react'
import { Modal } from '@/components/common/Modal'
import { useToast } from '@/hooks/useToast'
import { useVisibleNav } from './Sidebar'

/**
 * Global keyboard shortcuts (spec 21 §3.3.6, E-UX). `?` opens the cheat-sheet. `g` then a letter
 * jumps to an area — only to areas the user's role can open (else a toast says why). `/` focuses
 * the page's list search. Ctrl/⌘ K (command palette) lives in CommandPalette. Shortcuts are off
 * while typing in a field.
 */
const CHORDS: { key: string; to: string; label: string }[] = [
  { key: 'd', to: '/dashboard', label: 'Dashboard' },
  { key: 'a', to: '/approvals', label: 'Approvals' },
  { key: 'e', to: '/employees', label: 'Employees' },
  { key: 'p', to: '/payroll', label: 'Payroll runs' },
  { key: 't', to: '/attendance', label: 'Attendance' },
  { key: 'l', to: '/ess/leaves', label: 'My leave' },
  { key: 'm', to: '/ess/profile', label: 'My profile' },
  { key: 'n', to: '/notifications', label: 'Notifications' },
]

function typing(t: EventTarget | null) {
  const el = t as HTMLElement | null
  if (!el) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

export function useGlobalShortcuts(openHelp: () => void) {
  const navigate = useNavigate()
  const toast = useToast()
  const sections = useVisibleNav()
  const pendingG = useRef<number | null>(null)

  useEffect(() => {
    const allowed = new Set(sections.flatMap((s) => s.items.map((i) => i.to)))
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return
      // Never while a dialog is open (its own keys win).
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return
      if (e.key === '?') { e.preventDefault(); openHelp(); return }
      if (e.key === '/') {
        const search = document.querySelector<HTMLInputElement>('main input[aria-label*="Search" i], main input[type="search"]')
        if (search) { e.preventDefault(); search.focus() }
        return
      }
      if (pendingG.current !== null) {
        window.clearTimeout(pendingG.current)
        pendingG.current = null
        const c = CHORDS.find((x) => x.key === e.key.toLowerCase())
        if (!c) return
        e.preventDefault()
        if (allowed.has(c.to)) navigate(c.to)
        else toast.info(`${c.label} is not available for your account.`)
        return
      }
      if (e.key === 'g') {
        pendingG.current = window.setTimeout(() => { pendingG.current = null }, 1200)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [sections, navigate, toast, openHelp])
}

const Kbd = ({ children }: { children: string }) => (
  <kbd className="inline-flex min-w-[1.6rem] items-center justify-center rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px] font-medium text-foreground shadow-control">{children}</kbd>
)

export function ShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)
  const rows: { keys: string[]; label: string }[] = [
    { keys: [isMac ? '⌘' : 'Ctrl', 'K'], label: 'Search pages, people and records' },
    { keys: ['?'], label: 'Show this list' },
    { keys: ['/'], label: 'Search the current list' },
    ...CHORDS.map((c) => ({ keys: ['g', c.key], label: `Go to ${c.label.toLowerCase()}` })),
    { keys: ['Esc'], label: 'Close a dialog or menu' },
  ]
  return (
    <Modal open={open} onClose={onClose} title={<span className="inline-flex items-center gap-2"><Keyboard className="size-4" />Keyboard shortcuts</span>} size="sm">
      <ul className="divide-y divide-border" data-testid="shortcuts-list">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-4 py-2 text-sm">
            <span className="text-foreground">{r.label}</span>
            <span className="flex shrink-0 items-center gap-1">{r.keys.map((k, i) => <span key={i} className="flex items-center gap-1">{i > 0 && <span className="text-xs text-muted-foreground">{r.keys[0] === 'g' ? 'then' : '+'}</span>}<Kbd>{k}</Kbd></span>)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground">Shortcuts are paused while you type in a field.</p>
    </Modal>
  )
}
